"use strict";
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelRepository = void 0;
var foundation_1 = require("@deqo/foundation");
var ModelChangeBuffer_1 = require("./ModelChangeBuffer");
var ModelOperation_1 = require("./ModelOperation");
var _a = ModelOperation_1.ModelOperation.codes, $xcreate = _a.$xcreate, $xdelete = _a.$xdelete, $xrestore = _a.$xrestore, $cset = _a.$cset, $cappend = _a.$cappend, $ctruncate = _a.$ctruncate;
/**
 * Represents an object that acts as the main point of storage for model data and provides an abstraction interface that enables reading the
 * current model state.  Model state is composed of three layers, committed, pending and staged.  Committed is the base layer, where the
 * committed data is stored; data that that has been verified as correct by the collaboration server.  Pending contains data that has been
 * submitted as a transaction of mutations by the local client but not yet confirmed.  Mutation is an active and ongoing mutation.  It is
 * possible, and indeed likely, to have all three in play at one time.  ModelRepository abstracts the complexity of these layers into a
 * single interface.
 *
 * Layers are stored in an array ordered explicitly as [Committed, Pending, Mutation].  If there is no Staged or Pending active then these
 * will be absent from the array.  There is always a Committed.  In order to optimize computing the current state of a model fragment (some
 * entity data), there are two strategies for composing the various layers into a single output; top down and bottom up.
 *
 * Top down is the most efficient strategy, as it is able to look first at the Mutation and then Pending before it looks at the Committed
 * for data.  When Staged is active, it will have the latest state, so it's most efficient to look here first.  However, some operations,
 * such as getState() will require starting at the Committed and working outwards, mutating the composed value at each layer.  This is the
 * bottom up strategy and is neccesary because ModelChangeBuffers that represent Pending and Staged layers only store changes.  They do
 * not contain a complete copy of an entity state with single changes applied.  If they did, a remote transaction arriving and changing the
 * Committed state would potentially be hidden by the Pending layer, which could lead to confusing behavior.
 */
var ModelRepository = /** @class */ (function () {
    function ModelRepository(committed) {
        this.pending = null;
        this.staging = null;
        this.mutating = null;
        this.childrenCache = new Map();
        this.stateCache = new Map();
        this.confirmed = committed;
        this.layers = [committed];
    }
    ModelRepository.prototype.beginMutation = function () {
        (0, foundation_1.assert)(!this.mutating, 'ModelRepository: beginMutation with existing mutating.');
        this.mutating = ModelChangeBuffer_1.ModelChangeBuffer.create();
        this.layers.push(this.mutating);
        return this.mutating;
    };
    ModelRepository.prototype.notifyMutationUpdated = function (_a) {
        var _b = __read(_a, 2), code = _b[0], targetId = _b[1];
        if (!code || !targetId)
            return;
        // For certain changes we need to invalidate our caches...
        switch (code) {
            case $xcreate: break;
            case $xdelete:
            case $xrestore:
                this.childrenCache.delete(targetId);
                this.stateCache.delete(targetId);
                break;
            case $cset:
                this.stateCache.delete(targetId);
                break;
            case $cappend:
            case $ctruncate:
                this.childrenCache.delete(targetId);
                break;
        }
    };
    ModelRepository.prototype.promoteMutationToStaging = function () {
        (0, foundation_1.assert)(!!this.mutating, 'ModelRepository: promoteMutationToStaging without mutating.');
        if (this.staging) {
            ModelChangeBuffer_1.ModelChangeBuffer.assign(this.staging, this.mutating);
            this.layers.pop();
        }
        else {
            this.staging = this.mutating;
        }
        this.mutating = null;
    };
    ModelRepository.prototype.cancelMutation = function () {
        if (this.mutating) {
            this.mutating = null;
            this.layers.pop();
            this.purgeCaches();
        }
    };
    ModelRepository.prototype.promoteStagingToPending = function () {
        (0, foundation_1.assert)(!!this.staging, 'ModelRepository: promoteStagingToPending without staging.');
        (0, foundation_1.assert)(!this.pending, 'ModelRepository: promoteStagingToPending with existing pending.');
        this.pending = this.staging;
        this.staging = null;
    };
    ModelRepository.prototype.promotePendingToConfirmed = function (newVersion) {
        (0, foundation_1.assert)(!!this.pending, 'ModelRepository: promotePendingToConfirmed without pending.');
        ModelChangeBuffer_1.ModelChangeBuffer.apply(this.confirmed, this.pending);
        this.confirmed.version = newVersion;
        this.pending = null;
        this.layers.splice(1, 1);
        if (this.layers.length === 1) {
            this.purgeCaches();
        }
    };
    ModelRepository.prototype.applyConfirmedWrites = function (newVersion, writes) {
        this.confirmed.version = newVersion;
        ModelOperation_1.ModelOperation.execAll(this.confirmed, writes);
        this.purgeCaches();
    };
    ModelRepository.prototype.getVersion = function () {
        return this.confirmed.version;
    };
    ModelRepository.prototype.getList = function () {
        var e_1, _a, e_2, _b;
        // This is pretty ineffecient for a lot of reasons but shouldn't be called very often...
        // Bottom up:
        var im = {};
        try {
            for (var _c = __values(this.layers), _d = _c.next(); !_d.done; _d = _c.next()) {
                var ml = _d.value;
                try {
                    for (var _e = (e_2 = void 0, __values(Object.entries(ml.instances))), _f = _e.next(); !_f.done; _f = _e.next()) {
                        var _g = __read(_f.value, 2), id = _g[0], inst = _g[1];
                        if (inst === null)
                            delete im[id];
                        else
                            im[id] = inst;
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_1) throw e_1.error; }
        }
        // TODO: Cache here??
        return Object.keys(im);
    };
    ModelRepository.prototype.getInfo = function (entityId) {
        var info = this.getUsingTopDown(function (ml) { return ml.instances[entityId]; });
        if (info)
            return info;
        throw new Error("ModelRepository: ".concat(entityId, " does not exist."));
    };
    ModelRepository.prototype.exists = function (entityId) {
        // TopDown strategy:
        for (var i = this.layers.length - 1; i >= 0; i--) {
            var ml = this.layers[i];
            var entry = ml.instances[entityId];
            // If explicitly nulled, it was deleted.
            if (entry === null)
                return false;
            // If not undefined, it exists in this layer.
            if (entry !== undefined)
                return true;
        }
        // If not defined anywhere, can't exist.
        return false;
    };
    ModelRepository.prototype.getStateValue = function (entityId, key) {
        // Do we have a cached state?
        var cachedState = this.stateCache.get(entityId);
        if (cachedState)
            return cachedState[key];
        // Otherwise, use TopDown strategy:
        for (var i = this.layers.length - 1; i >= 0; i--) {
            var ml = this.layers[i];
            // Get the state fragment for this layer.
            var state = ml.states[entityId];
            if (state) {
                // If undefined we should fall through UNLESS entity was defined on this layer in which case we should return null since the
                // entity won't exist at layers below.
                var value = state[key];
                if (value !== undefined || !!ml.instances[entityId])
                    return value;
            }
            // If we are at the last layer OR state nulled out then entity must not exist.
            else if (i === 0 || state === null) {
                throw new Error("ModelRepository: ".concat(entityId, " does not exist."));
            }
        }
        return undefined;
    };
    ModelRepository.prototype.getState = function (entityId) {
        var e_3, _a, e_4, _b;
        // If only committed layer, we can just return the state as is...
        if (this.layers.length === 1) {
            return this.layers[0].states[entityId];
        }
        // Otherwise, do we have a cached state?
        var cs = this.stateCache.get(entityId);
        if (cs) {
            return cs;
        }
        // If not, build state object using BottomUp:
        var st = {};
        try {
            for (var _c = __values(this.layers), _d = _c.next(); !_d.done; _d = _c.next()) {
                var ml = _d.value;
                var entry = ml.states[entityId];
                if (entry) {
                    try {
                        for (var _e = (e_4 = void 0, __values(Object.keys(entry))), _f = _e.next(); !_f.done; _f = _e.next()) {
                            var key = _f.value;
                            if (entry[key] === null)
                                delete st[key];
                            else
                                st[key] = entry[key];
                        }
                    }
                    catch (e_4_1) { e_4 = { error: e_4_1 }; }
                    finally {
                        try {
                            if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                        }
                        finally { if (e_4) throw e_4.error; }
                    }
                }
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_3) throw e_3.error; }
        }
        // Cache before we return
        this.stateCache.set(entityId, st);
        return st;
    };
    ModelRepository.prototype.getParent = function (entityId) {
        var _a;
        return (_a = this.getUsingTopDown(function (ml) { return ml.parentLinks[entityId]; })) !== null && _a !== void 0 ? _a : undefined;
    };
    ModelRepository.prototype.getChildren = function (entityId) {
        var e_5, _a, e_6, _b;
        // If only committed layer, we can just return the children as is...
        if (this.layers.length === 1) {
            return this.confirmed.childLinks[entityId];
        }
        // Otherwise, do we have a cached children?
        var cc = this.childrenCache.get(entityId);
        if (cc) {
            return cc;
        }
        // If not, build children object using BottomUp:
        var cl = {};
        try {
            for (var _c = __values(this.layers), _d = _c.next(); !_d.done; _d = _c.next()) {
                var ml = _d.value;
                var entry = ml.childLinks[entityId];
                if (entry) {
                    try {
                        for (var _e = (e_6 = void 0, __values(Object.keys(entry))), _f = _e.next(); !_f.done; _f = _e.next()) {
                            var key = _f.value;
                            if (entry[key] === null)
                                delete cl[key];
                            else
                                cl[key] = entry[key];
                        }
                    }
                    catch (e_6_1) { e_6 = { error: e_6_1 }; }
                    finally {
                        try {
                            if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                        }
                        finally { if (e_6) throw e_6.error; }
                    }
                }
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_5) throw e_5.error; }
        }
        // Cache before we return
        this.childrenCache.set(entityId, cl);
        return cl;
    };
    ModelRepository.prototype.getUsingTopDown = function (getter) {
        for (var i = this.layers.length - 1; i >= 0; i--) {
            var value = getter(this.layers[i], i);
            if (value !== undefined)
                return value;
        }
        return undefined;
    };
    ModelRepository.prototype.purgeCaches = function () {
        this.childrenCache.clear();
        this.stateCache.clear();
    };
    return ModelRepository;
}());
exports.ModelRepository = ModelRepository;
// const buildListFromWeightedRecord = (wr: Record<string, string>): readonly string[] => {
//     const list = [] as string[];
//     for (const id of Object.keys(wr)) {
//         let added = false;
//         for (const x of list) {
//             if (wr[id] < wr[x]) {
//                 list.push(id);
//                 added = true;
//                 break;
//             }
//         }
//         if (!added) {
//             list.push(id);
//         }
//     }
//     return list;
// };
